import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Heart, Phone, Mail, Send } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { api, telHref } from "../lib/api";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import { Label } from "./ui/label";

const CRISIS_NUMBERS = [
  { region: "Auckland (Central)", numbers: ["0800 800 717"] },
  { region: "Auckland (East and South)", numbers: ["09 261 3700", "0800 775 222"] },
  { region: "Henderson", numbers: ["09 822 8501"] },
  { region: "North Shore", numbers: ["09 486 8900"] },
  { region: "Rodney", numbers: ["09 427 0360"] },
];

export const Footer = () => {
  const { t } = useLang();
  const [contactEmail, setContactEmail] = useState("");
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get("/site-info").then((r) => setContactEmail(r.data.contact_email)).catch(() => {});
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setSending(true);
    try {
      await api.post("/messages", form);
      toast.success(t("contact_ok"));
      setForm({ name: "", email: "", message: "" });
    } catch {
      toast.error("Could not send your message. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <footer className="mt-20 border-t border-stone-200 bg-[#F5F3F0]">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12">
        {/* Contact section */}
        <div id="contact" className="grid lg:grid-cols-2 gap-8 mb-12 scroll-mt-24" data-testid="contact-section">
          <div>
            <h3 className="font-display text-2xl font-extrabold text-stone-900">{t("contact_title")}</h3>
            <p className="mt-2 text-stone-600 leading-relaxed max-w-md">{t("contact_intro")}</p>
            {contactEmail && (
              <a href={`mailto:${contactEmail}`} data-testid="contact-email-link"
                className="mt-4 inline-flex items-center gap-2 text-[#2E604A] font-semibold hover:underline">
                <Mail className="w-4 h-4" /> {contactEmail}
              </a>
            )}
          </div>
          <form onSubmit={submit} className="bg-white rounded-2xl border border-stone-200 shadow-sm p-5 space-y-3">
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-sm font-semibold mb-1.5 block">{t("c_name")}</Label>
                <Input data-testid="contact-name" value={form.name} onChange={set("name")} className="rounded-xl h-11" />
              </div>
              <div>
                <Label className="text-sm font-semibold mb-1.5 block">{t("c_email")}</Label>
                <Input data-testid="contact-email" type="email" value={form.email} onChange={set("email")} className="rounded-xl h-11" />
              </div>
            </div>
            <div>
              <Label className="text-sm font-semibold mb-1.5 block">{t("c_message")}</Label>
              <Textarea data-testid="contact-message" value={form.message} onChange={set("message")} rows={3} className="rounded-xl" />
            </div>
            <Button type="submit" data-testid="contact-send" disabled={sending}
              className="w-full rounded-full bg-[#2E604A] hover:bg-[#234B3A] h-11 gap-2 font-semibold">
              <Send className="w-4 h-4" /> {t("c_send")}
            </Button>
          </form>
        </div>

        {/* Crisis numbers */}
        <div className="rounded-2xl bg-[#FDF0ED] border border-[#C05E4B]/20 p-5 sm:p-6 mb-8" data-testid="crisis-numbers">
          <div className="flex items-center gap-2.5 mb-4">
            <span className="w-9 h-9 rounded-xl bg-[#C05E4B]/10 text-[#C05E4B] flex items-center justify-center shrink-0">
              <Phone className="w-5 h-5" />
            </span>
            <h3 className="font-display font-bold text-stone-900 text-base sm:text-lg">{t("crisis_title")}</h3>
          </div>
          <p className="text-xs font-bold uppercase tracking-[0.1em] text-stone-500 mb-3">Auckland</p>
          <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-2.5">
            {CRISIS_NUMBERS.map((item) => (
              <li key={item.region} className="text-sm text-stone-700 leading-relaxed">
                <span className="font-semibold text-stone-900">{item.region}</span>
                {" — "}
                {item.numbers.map((num, i) => (
                  <span key={num}>
                    {i > 0 && <span className="text-stone-500"> or </span>}
                    <a href={`tel:${telHref(num)}`} data-testid={`crisis-tel-${telHref(num)}`}
                      className="font-semibold text-[#C05E4B] hover:underline">{num}</a>
                  </span>
                ))}
              </li>
            ))}
          </ul>
        </div>

        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <span className="w-8 h-8 rounded-lg bg-[#2E604A] text-white flex items-center justify-center">
              <Heart className="w-4 h-4" fill="currentColor" strokeWidth={0} />
            </span>
            <span className="font-display font-bold text-stone-900">{t("brand")}</span>
          </div>
          <p className="text-xs text-stone-500 max-w-md sm:text-right">
            A community directory for Auckland, Aotearoa New Zealand. Contact details are provided for convenience; please confirm hours directly with each organisation.
          </p>
        </div>
      </div>
    </footer>
  );
};
